import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Barcode,
  Check,
  ClipboardList,
  Minus,
  Package,
  Plus,
  RotateCcw,
  Search,
  Send,
  X,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  ProgressBar,
  StatusPill,
} from '../ui/index.js';
import {
  applyInventorySession,
  cancelInventorySession,
  createInventorySession,
  deleteInventoryLine,
  fetchInventoryBrands,
  fetchInventorySession,
  scanInventoryProduct,
  type InventoryProductDto,
  type InventorySessionDto,
} from '../api.js';

/**
 * S30 — Inventaire (full redo 2026-11-03).
 *
 * The counting loop is ONE gesture: a scan = +1, instantly, no modal. The
 * flash card under the scan input shows what was just counted (big number,
 * −1 undo, exact-quantity entry). The sheet splits into « Comptés » and
 * « Restants » so the operator always knows what is left. Manual entry from
 * the sheet SETS the absolute quantity. Applying to Woo goes through the
 * verified pipeline after an explicit review.
 */

type SheetTab = 'counted' | 'remaining';

interface FlashCard {
  readonly productId: number;
  readonly total: number;
  readonly expectedQty: number | null;
}

interface RecentScan {
  readonly productId: number;
  readonly name: string;
  readonly sku: string;
  readonly image: string | null;
  readonly delta: number;
  readonly total: number;
  readonly at: number;
}

const sessionKey = (sessionId: number | null): readonly unknown[] => ['inventory-session', sessionId];

function ProductThumb({ src, size = 36 }: { readonly src: string | undefined; readonly size?: number }): React.ReactElement {
  const px = { width: size, height: size };
  if (src === undefined || src === '') {
    return (
      <span
        className="grid shrink-0 place-items-center rounded-lg border border-dashed border-border text-ink-ghost"
        style={px}
        aria-hidden="true"
      >
        <Package style={{ width: size * 0.45, height: size * 0.45 }} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="shrink-0 rounded-lg border border-border bg-white object-contain p-0.5"
      style={px}
    />
  );
}

/** Écart pill: counted vs expected. Missing stock = ko, surplus = warn. */
function VariancePill({ delta }: { readonly delta: number }): React.ReactElement | null {
  if (delta === 0) return null;
  return (
    <StatusPill tone={delta < 0 ? 'ko' : 'warn'}>
      {delta > 0 ? `+${delta}` : String(delta)}
    </StatusPill>
  );
}

export function InventoryPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [brandChoice, setBrandChoice] = useState('');
  const [code, setCode] = useState('');
  const [flash, setFlash] = useState<FlashCard | null>(null);
  const [recent, setRecent] = useState<RecentScan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<SheetTab>('counted');
  const [filterText, setFilterText] = useState('');
  const [edit, setEdit] = useState<{ product: InventoryProductDto; value: string } | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const brandsQuery = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: fetchInventoryBrands,
  });

  const sessionQuery = useQuery({
    queryKey: sessionKey(sessionId),
    queryFn: () => fetchInventorySession(sessionId as number),
    enabled: sessionId !== null,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['inventory-session'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory-brands'] });
  };

  /** Instant UI: patch the cached sheet with the scan response — no refetch. */
  const patchSheet = (
    product: InventoryProductDto,
    line: { quantity: number; scans: number; expectedQty: number | null },
  ): void => {
    if (sessionId === null) return;
    void queryClient.setQueryData<{ session: InventorySessionDto; products: readonly InventoryProductDto[]; countedCount: number }>(
      sessionKey(sessionId),
      (old) =>
        old === undefined
          ? old
          : {
              ...old,
              products: old.products.map((p) =>
                p.id === product.id
                  ? { ...p, counted: true, countedQuantity: line.quantity, scans: line.scans, expectedQty: line.expectedQty ?? p.expectedQty }
                  : p,
              ),
            },
    );
  };

  const pushRecent = (product: InventoryProductDto, delta: number, total: number): void => {
    setRecent((list) =>
      [
        {
          productId: product.id,
          name: product.name,
          sku: product.sku,
          image: product.images[0] ?? null,
          delta,
          total,
          at: Date.now(),
        },
        ...list.filter((r) => r.productId !== product.id),
      ].slice(0, 8),
    );
  };

  const startSession = useMutation({
    mutationFn: (brandName: string) => createInventorySession(brandName),
    onSuccess: (res) => {
      setError(null);
      setRecent([]);
      setFlash(null);
      setSessionId(res.session.id);
      setTab('remaining');
      window.setTimeout(() => scanInputRef.current?.focus(), 120);
    },
    onError: (err: Error) => setError(err.message),
  });

  const applyLine = useMutation({
    mutationFn: (input: { productId: number; quantity: number; mode: 'add' | 'set' }) =>
      scanInventoryProduct(sessionId as number, '', input.productId, input.quantity, input.mode),
    onSuccess: (res, input) => {
      setError(null);
      patchSheet(res.product, res.line);
      setFlash({ productId: res.product.id, total: res.line.quantity, expectedQty: res.line.expectedQty });
      if (input.mode === 'add') pushRecent(res.product, input.quantity, res.line.quantity);
      setEdit(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const scan = useMutation({
    mutationFn: (input: { code: string }) => scanInventoryProduct(sessionId as number, input.code, undefined, 1, 'add'),
    onSuccess: (res) => {
      setError(null);
      setCode('');
      patchSheet(res.product, res.line);
      setFlash({ productId: res.product.id, total: res.line.quantity, expectedQty: res.line.expectedQty });
      pushRecent(res.product, 1, res.line.quantity);
    },
    onError: (err: Error) => {
      setError(err.message);
      setCode('');
      window.setTimeout(() => scanInputRef.current?.focus(), 60);
    },
  });

  const resetLine = useMutation({
    mutationFn: (productId: number) => deleteInventoryLine(sessionId as number, productId),
    onSuccess: (_res, productId) => {
      setError(null);
      if (sessionId === null) return;
      void queryClient.setQueryData<{ session: InventorySessionDto; products: readonly InventoryProductDto[]; countedCount: number }>(
        sessionKey(sessionId),
        (old) =>
          old === undefined
            ? old
            : {
                ...old,
                products: old.products.map((p) =>
                  p.id === productId ? { ...p, counted: false, countedQuantity: 0, scans: 0 } : p,
                ),
              },
      );
      setFlash(null);
      setRecent((list) => list.filter((r) => r.productId !== productId));
    },
    onError: (err: Error) => setError(err.message),
  });

  const apply = useMutation({
    mutationFn: () => applyInventorySession(sessionId as number),
    onSuccess: (res) => {
      setApplyOpen(false);
      setNotice(
        `Stock mis à jour sur le site : ${res.verified} produit(s) vérifié(s), ${res.failed} échec(s).`,
      );
      invalidate();
    },
    onError: (err: Error) => {
      setApplyOpen(false);
      setError(err.message);
    },
  });

  const closeSession = useMutation({
    mutationFn: () => cancelInventorySession(sessionId as number),
    onSuccess: () => {
      setSessionId(null);
      setFlash(null);
      setRecent([]);
      setNotice(null);
      invalidate();
    },
  });

  const session: InventorySessionDto | undefined = sessionQuery.data?.session;
  const products: readonly InventoryProductDto[] = useMemo(() => sessionQuery.data?.products ?? [], [sessionQuery.data]);
  const counted = useMemo(() => products.filter((p) => p.counted), [products]);
  const countedQtyTotal = counted.reduce((s, p) => s + p.countedQuantity, 0);
  const varianceTotal = counted.reduce((s, p) => (p.expectedQty === null ? s : s + (p.countedQuantity - p.expectedQty)), 0);
  const missingCount = counted.filter((p) => p.expectedQty !== null && p.countedQuantity - p.expectedQty < 0).length;
  const surplusCount = counted.filter((p) => p.expectedQty !== null && p.countedQuantity - p.expectedQty > 0).length;
  const progressPct = products.length === 0 ? 0 : Math.round((counted.length / products.length) * 100);

  const flashProduct = flash === null ? undefined : products.find((p) => p.id === flash.productId);

  const sheetProducts = useMemo(() => {
    const q = filterText.toLowerCase().trim();
    const base =
      tab === 'counted'
        ? products.filter((p) => p.counted)
        : products.filter((p) => !p.counted);
    if (q === '') return base;
    return base.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.gtinCanonical !== null && p.gtinCanonical.toLowerCase().includes(q)) ||
        (p.barcodeRaw !== null && p.barcodeRaw.toLowerCase().includes(q)),
    );
  }, [products, tab, filterText]);

  const openSessions = brandsQuery.data?.openSessions ?? [];
  const brands = brandsQuery.data?.brands ?? [];

  // The barcode gun types into the scan field: keep it focused while no
  // dialog is open and the operator is not deliberately typing elsewhere.
  useEffect(() => {
    if (sessionId === null) return;
    const focus = (): void => {
      const active = document.activeElement;
      if (edit !== null || applyOpen || closeOpen) return;
      if (active === null || active.tagName !== 'INPUT') {
        scanInputRef.current?.focus();
      }
    };
    const timer = window.setInterval(focus, 700);
    return () => window.clearInterval(timer);
  }, [sessionId, edit, applyOpen, closeOpen]);

  const submitScan = (event: React.FormEvent): void => {
    event.preventDefault();
    const clean = code.trim();
    if (clean === '' || sessionId === null) return;
    scan.mutate({ code: clean });
  };

  const openExactEdit = (product: InventoryProductDto): void => {
    setEdit({ product, value: String(product.countedQuantity) });
    window.setTimeout(() => editInputRef.current?.select(), 60);
  };

  const confirmExactEdit = (): void => {
    if (edit === null) return;
    const qty = Number(edit.value);
    if (!Number.isFinite(qty) || qty < 0) {
      setError('Quantité invalide.');
      return;
    }
    applyLine.mutate({ productId: edit.product.id, quantity: Math.trunc(qty), mode: 'set' });
  };

  // ---------------- Home: pick a brand / resume ----------------
  if (sessionId === null) {
    return (
      <div>
        <PageHeader
          index="S30"
          title="Inventaire"
          purpose="Comptez une marque au fusil : un scan = +1, sans fenêtre. Contrôlez les écarts, puis appliquez le stock au site en une écriture vérifiée."
        />
        {notice !== null && (
          <div className="mb-4">
            <Alert
              tone="good"
              title="Terminé"
              actions={
                <button
                  type="button"
                  onClick={() => setNotice(null)}
                  className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                  aria-label="Fermer"
                >
                  ✕
                </button>
              }
            >
              {notice}
            </Alert>
          </div>
        )}
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel>
            <PanelBody className="space-y-4">
              <div>
                <label htmlFor="inventory-brand" className="mb-1.5 block text-sm font-bold text-ink">
                  Nouveau comptage — choisissez la marque
                </label>
                <select
                  id="inventory-brand"
                  value={brandChoice}
                  onChange={(e) => setBrandChoice(e.target.value)}
                  className="h-12 w-full rounded-xl border border-border bg-card px-3 text-sm font-semibold text-ink shadow-xs"
                >
                  <option value="">— Choisir une marque —</option>
                  {brands.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand} ({b.count} produits)
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-ink-soft">
                  La session regroupe la marque et toutes ses sous-marques. Le stock actuel du site est
                  mémorisé comme référence pour calculer les écarts.
                </p>
              </div>
              <Button
                variant="primary"
                size="md"
                className="h-12 w-full"
                disabled={brandChoice === '' || startSession.isPending}
                onClick={() => startSession.mutate(brandChoice)}
              >
                <ClipboardList className="h-5 w-5" aria-hidden="true" />
                {startSession.isPending ? 'Ouverture…' : 'Commencer le comptage'}
              </Button>
              {brandsQuery.isLoading && <Loading label="Lecture des marques…" />}
              {brandsQuery.data !== undefined && brands.length === 0 && (
                <EmptyState
                  icon={<Package className="h-6 w-6" aria-hidden="true" />}
                  title="Aucune marque à inventorier"
                  hint="Publiez des produits ou synchronisez d'abord le catalogue WooCommerce."
                />
              )}
            </PanelBody>
          </Panel>

          <Panel>
            <PanelBody className="space-y-3">
              <div className="text-sm font-bold text-ink">Sessions ouvertes</div>
              {openSessions.length === 0 && (
                <EmptyState
                  icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
                  title="Aucune session en cours"
                  hint="Les comptages non appliqués apparaissent ici pour être repris."
                />
              )}
              <ul className="space-y-2">
                {openSessions.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-card px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge tone="scan">{s.brand}</Badge>
                        <span className="text-xs text-ink-faint">
                          ouverte par {s.startedBy}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-ink-soft">
                        {new Date(s.startedAt).toLocaleString('fr-FR')}
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setSessionId(s.id);
                        setTab('counted');
                        window.setTimeout(() => scanInputRef.current?.focus(), 120);
                      }}
                    >
                      Reprendre
                    </Button>
                  </li>
                ))}
              </ul>
            </PanelBody>
          </Panel>
        </div>
      </div>
    );
  }

  // ---------------- Session: the counting screen ----------------
  const applied = session?.status === 'applied';

  return (
    <div>
      <PageHeader
        index="S30"
        title="Inventaire"
        purpose="Un scan = +1. Contrôlez les écarts feuille en main, puis appliquez au site."
        meta={[
          { label: 'Marque', value: session?.brand ?? '—' },
          { label: 'Comptés', value: `${counted.length} / ${products.length}` },
          {
            label: 'Écart',
            value:
              varianceTotal === 0 ? '0' : `${varianceTotal > 0 ? '+' : ''}${varianceTotal}`,
          },
        ]}
      />

      {error !== null && (
        <div className="mb-4">
          <Alert
            tone="alarm"
            title="Erreur"
            actions={
              <button
                type="button"
                onClick={() => setError(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {error}
          </Alert>
        </div>
      )}
      {notice !== null && (
        <div className="mb-4">
          <Alert
            tone="good"
            title="Appliqué"
            actions={
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {notice}
          </Alert>
        </div>
      )}

      {/* Session bar */}
      <Panel className="mb-4">
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="scan">{session?.brand}</Badge>
            <span className="text-sm font-semibold text-ink">
              {counted.length} compté(s) · {countedQtyTotal} unité(s)
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              {missingCount > 0 && <StatusPill tone="ko">{missingCount} en manque</StatusPill>}
              {surplusCount > 0 && <StatusPill tone="warn">{surplusCount} en surplus</StatusPill>}
            </span>
            <span className="flex-1" />
            {!applied && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={counted.length === 0 || apply.isPending}
                  onClick={() => setApplyOpen(true)}
                  title="Envoie les quantités comptées au stock du site (écriture vérifiée)"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Appliquer au site
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCloseOpen(true)}>
                  <X className="h-4 w-4" aria-hidden="true" />
                  Terminer
                </Button>
              </>
            )}
            {applied && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSessionId(null);
                  setNotice(null);
                  invalidate();
                }}
              >
                Retour à l'accueil
              </Button>
            )}
          </div>
          <ProgressBar value={progressPct} label={`${progressPct}% de la marque comptée`} />
        </PanelBody>
      </Panel>

      {applied ? (
        <Panel>
          <PanelBody>
            <EmptyState
              icon={<Check className="h-6 w-6" aria-hidden="true" />}
              title="Session appliquée"
              hint={`Le stock WooCommerce de la marque ${session?.brand} a été remplacé par les quantités comptées${session?.appliedBy !== null && session?.appliedBy !== undefined ? ` par ${session.appliedBy}` : ''}.`}
            />
          </PanelBody>
        </Panel>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          {/* ---------- Scan zone ---------- */}
          <div className="space-y-4">
            <Panel>
              <PanelBody className="space-y-3">
                <form onSubmit={submitScan}>
                  <label htmlFor="inventory-scan" className="mb-1.5 block text-sm font-bold text-ink">
                    Scanner (un scan = +1)
                  </label>
                  <div className="relative">
                    <Barcode
                      className="pointer-events-none absolute left-4 top-1/2 h-6 w-6 -translate-y-1/2 text-ink-ghost"
                      aria-hidden="true"
                    />
                    <Input
                      id="inventory-scan"
                      ref={scanInputRef}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Le fusil saisit le code et valide…"
                      autoComplete="off"
                      spellCheck={false}
                      className="h-14 pl-12 text-lg font-mono"
                    />
                  </div>
                </form>

                {/* Flash card: the just-scanned product */}
                {flashProduct !== undefined && flash !== null ? (
                  <div className="rounded-xl border-2 border-herb-500 bg-herb-50/60 p-4">
                    <div className="flex items-center gap-4">
                      <ProductThumb src={flashProduct.images[0]} size={64} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-bold leading-snug text-ink" title={flashProduct.name}>
                          {flashProduct.name}
                        </p>
                        <p className="font-mono text-xs text-ink-faint">{flashProduct.sku}</p>
                        <p className="mt-0.5 text-xs text-ink-soft">
                          stock site : {flashProduct.expectedQty ?? '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-5xl font-black leading-none text-herb-700 tabular">
                          {flash.total}
                        </div>
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-ink-faint">
                          compté(s)
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() => applyLine.mutate({ productId: flashProduct.id, quantity: 1, mode: 'add' })}
                        disabled={applyLine.isPending}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        Encore +1
                      </Button>
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() => applyLine.mutate({ productId: flashProduct.id, quantity: -1, mode: 'add' })}
                        disabled={applyLine.isPending || flash.total === 0}
                      >
                        <Minus className="h-4 w-4" aria-hidden="true" />
                        Retirer 1
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openExactEdit(flashProduct)}>
                        Quantité exacte…
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resetLine.mutate(flashProduct.id)}
                        disabled={resetLine.isPending}
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                        Annuler le comptage
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-border bg-cream/40 px-4 py-6 text-center text-sm text-ink-soft">
                    Scannez un code-barres — la carte du produit compté apparaît ici.
                  </div>
                )}
              </PanelBody>
            </Panel>

            {/* Recent scans feed */}
            {recent.length > 0 && (
              <Panel>
                <PanelBody>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-faint">
                    Derniers scans
                  </div>
                  <ul className="divide-y divide-border">
                    {recent.map((r) => (
                      <li key={r.productId} className="flex items-center gap-3 py-2">
                        <ProductThumb src={r.image ?? undefined} size={32} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-ink" title={r.name}>
                            {r.name}
                          </p>
                          <p className="font-mono text-[11px] text-ink-faint">{r.sku}</p>
                        </div>
                        <StatusPill tone={r.delta > 0 ? 'ok' : 'ko'}>
                          {r.delta > 0 ? `+${r.delta}` : r.delta} → {r.total}
                        </StatusPill>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => applyLine.mutate({ productId: r.productId, quantity: -1, mode: 'add' })}
                          disabled={applyLine.isPending || r.total === 0}
                          title="Retirer 1"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </PanelBody>
              </Panel>
            )}
          </div>

          {/* ---------- The sheet ---------- */}
          <Panel className="self-start">
            <PanelBody className="space-y-3">
              <div className="flex items-center gap-1 rounded-xl bg-cream p-1">
                <button
                  type="button"
                  onClick={() => setTab('counted')}
                  className={`h-9 flex-1 rounded-lg text-sm font-bold transition-colors cursor-pointer ${
                    tab === 'counted' ? 'bg-card text-ink shadow-xs' : 'text-ink-soft hover:text-ink'
                  }`}
                >
                  Comptés ({counted.length})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('remaining')}
                  className={`h-9 flex-1 rounded-lg text-sm font-bold transition-colors cursor-pointer ${
                    tab === 'remaining' ? 'bg-card text-ink shadow-xs' : 'text-ink-soft hover:text-ink'
                  }`}
                >
                  Restants ({products.length - counted.length})
                </button>
              </div>

              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost"
                  aria-hidden="true"
                />
                <Input
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Filtrer par nom, SKU, code-barres…"
                  className="pl-9"
                />
              </div>

              {sessionQuery.isLoading && <Loading label="Lecture de la feuille…" />}
              {sessionQuery.isError && (
                <Alert tone="alarm" title="Feuille indisponible">
                  {String(sessionQuery.error.message)}
                </Alert>
              )}

              <ul className="max-h-[560px] divide-y divide-border overflow-y-auto rounded-xl border border-border">
                {sheetProducts.map((p) => {
                  const delta = p.expectedQty === null ? null : p.countedQuantity - p.expectedQty;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => openExactEdit(p)}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-cream/60 cursor-pointer"
                      >
                        <ProductThumb src={p.images[0]} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-ink" title={p.name}>
                            {p.name}
                          </p>
                          <p className="font-mono text-[11px] text-ink-faint">
                            {p.sku}
                            {p.gtinCanonical !== null ? ` · ${p.gtinCanonical}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-ink tabular">{p.countedQuantity}</div>
                          <div className="text-[11px] text-ink-faint tabular">
                            site : {p.expectedQty ?? '—'}
                          </div>
                        </div>
                        {delta !== null && p.counted && <VariancePill delta={delta} />}
                        {p.counted && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              resetLine.mutate(p.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                resetLine.mutate(p.id);
                              }
                            }}
                            className="rounded-lg p-1.5 text-ink-ghost transition-colors hover:bg-rose-50 hover:text-rose-700 cursor-pointer"
                            title="Annuler le comptage"
                            aria-label={`Annuler le comptage de ${p.name}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {sheetProducts.length === 0 && !sessionQuery.isLoading && (
                <EmptyState
                  icon={<Search className="h-6 w-6" aria-hidden="true" />}
                  title={tab === 'counted' ? 'Aucun produit compté' : 'Tout est compté'}
                  hint={
                    filterText.trim() !== ''
                      ? `Aucun produit ne correspond à « ${filterText} ».`
                      : tab === 'counted'
                        ? 'Scannez des codes-barres ou touchez un produit dans « Restants » pour saisir sa quantité.'
                        : 'Tous les produits de la marque ont été comptés — appliquez au site quand c’est prêt.'
                  }
                />
              )}

              <p className="text-xs text-ink-soft">
                Touchez un produit pour saisir sa quantité exacte — le scan reste le chemin rapide.
              </p>
            </PanelBody>
          </Panel>
        </div>
      )}

      {/* Exact-quantity modal (deliberate entry, not part of the scan loop) */}
      {edit !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-fade"
            onClick={() => setEdit(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Quantité exacte"
              className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl animate-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <ProductThumb src={edit.product.images[0]} size={64} />
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold leading-snug text-ink">{edit.product.name}</p>
                  <p className="font-mono text-xs text-ink-faint">{edit.product.sku}</p>
                  <p className="mt-1 text-xs text-ink-soft">
                    Stock site : <strong>{edit.product.expectedQty ?? '—'}</strong> — la valeur saisie
                    REMPLACE le comptage actuel ({edit.product.countedQuantity}).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEdit(null)}
                  className="rounded-full p-1.5 text-ink-ghost transition-colors hover:bg-cream hover:text-ink cursor-pointer"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <Button
                  type="button"
                  variant="soft"
                  size="md"
                  className="h-14 w-14 shrink-0"
                  onClick={() => setEdit({ ...edit, value: String(Math.max(0, Number(edit.value || '0') - 1)) })}
                  aria-label="Diminuer"
                >
                  <Minus className="h-5 w-5" />
                </Button>
                <Input
                  ref={editInputRef}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={edit.value}
                  onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmExactEdit();
                    }
                    if (e.key === 'Escape') setEdit(null);
                  }}
                  className="h-14 flex-1 text-center text-3xl font-black tabular"
                />
                <Button
                  type="button"
                  variant="soft"
                  size="md"
                  className="h-14 w-14 shrink-0"
                  onClick={() => setEdit({ ...edit, value: String(Number(edit.value || '0') + 1) })}
                  aria-label="Augmenter"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEdit(null)}>
                  Fermer
                </Button>
                <Button variant="primary" size="md" disabled={applyLine.isPending} onClick={confirmExactEdit}>
                  <Check className="h-4 w-4" aria-hidden="true" />
                  {applyLine.isPending ? 'Enregistrement…' : 'Définir la quantité'}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <ConfirmDialog
        open={applyOpen}
        tone="alarm"
        title="Appliquer l'inventaire au site"
        confirmLabel="Appliquer au site"
        busy={apply.isPending}
        onConfirm={() => apply.mutate()}
        onCancel={() => setApplyOpen(false)}
      >
        <div className="space-y-2 text-sm leading-relaxed text-ink">
          <p>
            Le stock du site des <strong>{counted.length}</strong> produit(s) comptés de{' '}
            <strong>{session?.brand}</strong> sera remplacé par la quantité comptée.
          </p>
          {(missingCount > 0 || surplusCount > 0) && (
            <p>
              <span className="font-semibold text-rose-700">{missingCount} produit(s) en manque</span>
              {' · '}
              <span className="font-semibold text-amber-700">{surplusCount} en surplus</span> par
              rapport au stock actuel.
            </p>
          )}
          <p className="text-xs text-ink-soft">
            Écriture vérifiée : journal WAL + relecture de chaque produit après envoi.
          </p>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={closeOpen}
        tone="alarm"
        title="Terminer sans appliquer ?"
        confirmLabel="Terminer la session"
        busy={closeSession.isPending}
        onConfirm={() => closeSession.mutate()}
        onCancel={() => setCloseOpen(false)}
      >
        <p className="text-sm leading-relaxed text-ink">
          La session <strong>{session?.brand}</strong> sera fermée sans toucher au stock du site. Les
          quantités comptées seront conservées dans l'historique mais la feuille ne sera plus
          modifiable.
        </p>
      </ConfirmDialog>
    </div>
  );
}
